<template>
  <el-dialog
    :title="isEdit ? '编辑监控任务' : '新建监控任务'"
    :visible.sync="dialogVisible"
    width="960px"
    top="5vh"
    :close-on-click-modal="false"
    append-to-body
    class="binding-form-dialog"
  >
    <div v-loading="loading" class="form-body">
      <!-- 任务名称 -->
      <div class="task-name-row">
        <span class="task-name-label"><i class="el-icon-edit-outline"></i> 任务名称</span>
        <el-input
          v-model="form.name"
          placeholder="请输入任务名称"
          size="small"
          clearable
          maxlength="50"
          show-word-limit
          style="width: 320px;"
        />
        <span class="task-name-tip">必填项，用于标识该监控任务</span>
      </div>
      <!-- Steps -->
      <el-steps :active="currentStep" finish-status="success" align-center class="steps-bar">
        <el-step title="选择线路" />
        <el-step title="选择车辆" />
        <el-step title="生效时间" />
        <el-step title="告警策略" />
      </el-steps>

      <!-- Step 1: 线路 -->
      <div v-show="currentStep === 0" class="step-panel">
        <div class="step-desc">选择本任务要绑定的基准线路。可以按名称/编号搜索，右侧小地图预览线路走向与总里程。</div>

        <div class="step1-layout">
          <div class="route-list-wrap">
            <div class="route-search">
              <el-input
                v-model="routeSearchKw"
                placeholder="搜索线路名称或编号"
                size="small"
                clearable
                prefix-icon="el-icon-search"
                style="width: 100%;"
              />
            </div>
            <div class="route-list">
              <div
                v-for="r in filteredRoutes"
                :key="r.id"
                class="route-card"
                :class="{ active: form.routeId === r.id }"
                @click="selectRoute(r)"
              >
                <div class="rc-name">
                  <span v-if="form.routeId === r.id" class="rc-check"><i class="el-icon-check"></i></span>
                  {{ r.name }}
                </div>
                <div class="rc-meta">
                  <span v-if="r.code" class="rc-code">{{ r.code }}</span>
                  <span class="rc-len"><i class="el-icon-guide"></i> {{ r.totalLengthKm }} km</span>
                  <span class="rc-pts">{{ r.pointCount }} 点</span>
                  <span>
                    <el-tag size="mini" :type="r.status === 1 ? 'success' : 'info'" style="transform: scale(0.85);">
                      {{ r.statusText }}
                    </el-tag>
                  </span>
                </div>
                <div v-if="r.sourceVehiclePlate" class="rc-sub">来源车辆：{{ r.sourceVehiclePlate }}</div>
                <div v-if="r.extraPointCount" class="rc-sub">附加点：{{ r.extraPointCount }} 个</div>
              </div>
              <div v-if="!filteredRoutes.length" class="empty-tip">暂无匹配线路</div>
            </div>
          </div>
          <div class="route-preview-wrap">
            <div class="preview-title">
              <i class="el-icon-map-location"></i> 线路预览
              <span v-if="selectedRoute" class="preview-meta">
                <el-tag size="mini" style="margin-left:8px;">{{ selectedRoute.totalLengthKm }} km</el-tag>
                <el-tag size="mini" type="info" style="margin-left:4px;">{{ selectedRoute.pointCount }} 点</el-tag>
              </span>
            </div>
            <div class="preview-map" :class="{ 'with-route': !!selectedRoute }">
              <KingLongMap
                ref="previewMap"
                :mapId="'bindingPreviewMap'"
                @LoadCompleted="onPreviewMapLoaded"
              />
              <div v-if="!selectedRoute" class="preview-empty-overlay">
                <i class="el-icon-place"></i>
                <div>请先选择一条线路</div>
              </div>
              <div v-if="selectedRoute" class="preview-legend">
                <span class="lg-item"><span class="lg-line blue"></span>线路轨迹</span>
                <span class="lg-item"><span class="lg-dot green"></span>起点</span>
                <span class="lg-item"><span class="lg-dot red"></span>终点</span>
                <span class="lg-item" v-if="selectedRoute.extraPointCount"><span class="lg-buf"></span>豁免/途经点</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 2: 车辆 -->
      <div v-show="currentStep === 1" class="step-panel">
        <div class="step-desc">
          选择要纳入本任务监控的车辆。支持按车牌/驾驶员搜索，也可按车组批量勾选。
          <span v-if="conflicts.length" class="warn-inline">
            <i class="el-icon-warning"></i> 检测到 {{ conflicts.length }} 个车辆时间冲突（将以提示展示，不强制阻止）
          </span>
        </div>

        <div class="step2-layout">
          <div class="vehicle-pool">
            <div class="pool-toolbar">
              <el-input
                v-model="vehicleKw"
                placeholder="搜索车牌 / 驾驶员"
                size="small"
                clearable
                style="width: 220px;"
                prefix-icon="el-icon-search"
              />
              <div style="margin-left: auto;">
                <el-checkbox v-model="selectAll" :indeterminate="selectIndeterminate" size="small" @change="toggleSelectAll">全选当前</el-checkbox>
              </div>
            </div>
            <el-table
              ref="vehTbl"
              :data="filteredVehicles"
              size="small"
              height="360"
              border
              @selection-change="onVehicleSelection"
            >
              <el-table-column type="selection" width="45" />
              <el-table-column prop="plateNo" label="车牌" width="120" />
              <el-table-column prop="driver" label="驾驶员" width="90" />
              <el-table-column prop="driverPhone" label="电话" />
            </el-table>
          </div>

          <div class="selected-wrap">
            <div class="selected-title">
              <span>已选车辆 <el-tag size="mini" type="warning">{{ selectedVehicles.length }}</el-tag></span>
              <el-button size="mini" type="text" style="color:#F56C6C;" @click="selectedVehicles = []; syncSelection();">清空</el-button>
            </div>
            <div class="selected-list">
              <div v-if="!selectedVehicles.length" class="empty-tip">请从左侧勾选车辆</div>
              <div v-for="v in selectedVehicles" :key="v.vehicleId" class="sv-item"
                :class="{ conflict: conflicts.some(c => c.vehicleId === v.vehicleId) }">
                <div class="sv-main">
                  <span class="sv-plate">{{ v.plateNo }}</span>
                  <span class="sv-driver">{{ v.driver }}</span>
                </div>
                <i class="el-icon-close" @click="removeVehicle(v)"></i>
              </div>
            </div>
            <div v-if="conflicts.length" class="conflict-list">
              <div class="conflict-title"><i class="el-icon-warning"></i> 冲突提示</div>
              <div v-for="c in conflicts" :key="c.vehicleId" class="conflict-item">
                <b>{{ c.plateNo }}</b>：{{ c.overlapReason }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 3: 生效时间 -->
      <div v-show="currentStep === 2" class="step-panel">
       
        <el-form :model="form" label-width="130px" size="small" class="time-form">
          <!-- <el-form-item label="生效方式">
            <el-radio-group v-model="form.effectiveType">
              <el-radio :label="2">周期时间段（推荐）</el-radio>
              <el-radio :label="1">单次时间段</el-radio>
            </el-radio-group>
          </el-form-item> -->
          <el-form-item label="总有效期" required>
            <el-date-picker
              v-model="form._validRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              size="small"
              style="width: 380px;"
            />
            <span class="hint-text">整体有效期，超出后任务自动失效</span>
          </el-form-item>

          <template v-if="form.effectiveType === 2">
            <el-form-item label="星期">
              <el-checkbox-group v-model="form.weekdays">
                <el-checkbox :label="1">一</el-checkbox>
                <el-checkbox :label="2">二</el-checkbox>
                <el-checkbox :label="3">三</el-checkbox>
                <el-checkbox :label="4">四</el-checkbox>
                <el-checkbox :label="5">五</el-checkbox>
                <el-checkbox :label="6">六</el-checkbox>
                <el-checkbox :label="7">日</el-checkbox>
              </el-checkbox-group>
              <div class="quick-btns" style="margin-left: 12px;">
                <el-button size="mini" @click="form.weekdays = [1,2,3,4,5]">工作日</el-button>
                <el-button size="mini" @click="form.weekdays = [6,7]">周末</el-button>
                <el-button size="mini" @click="form.weekdays = [1,2,3,4,5,6,7]">每日</el-button>
              </div>
            </el-form-item>
            <el-form-item label="每日时段">
              <div class="daily-slots">
                <div v-for="(slot, i) in form.dailySlots" :key="i" class="slot-item">
                  <el-time-picker
                    v-model="slot.start"
                    placeholder="开始"
                    value-format="HH:mm"
                    format="HH:mm"
                    size="small"
                    style="width: 140px;"
                  />
                  <span class="slot-sep">~</span>
                  <el-time-picker
                    v-model="slot.end"
                    placeholder="结束"
                    value-format="HH:mm"
                    format="HH:mm"
                    size="small"
                    style="width: 140px;"
                  />
                  <el-button size="mini" type="text" style="color:#F56C6C;" @click="removeSlot(i)" v-if="form.dailySlots.length > 1">
                    <i class="el-icon-delete"></i>
                  </el-button>
                </div>
                <el-button size="mini" type="primary" plain icon="el-icon-plus" @click="addSlot">新增时段</el-button>
              </div>
            </el-form-item>
          </template>

          <!-- <el-form-item label="时区">
            <el-select v-model="form.timezone" size="small" style="width: 220px;">
              <el-option label="Asia/Shanghai (中国标准时间)" value="Asia/Shanghai" />
              <el-option label="UTC" value="UTC" />
            </el-select>
          </el-form-item> -->
        </el-form>
      </div>

      <!-- Step 4: 告警策略 -->
      <div v-show="currentStep === 3" class="step-panel">
        <div class="step-desc">
          配置偏离判定的触发阈值、连续确认条件、告警级别与通知方式。推荐保留默认值。
        </div>
        <div class="strategy-split">
          <el-form :model="form" label-width="130px" size="small" class="strategy-form">
            <div class="form-group-title"><i class="el-icon-magic-stick"></i> 判定参数</div>
            <el-form-item label="偏离阈值 (米)" required>
              <el-slider v-model="form.thresholdM" :min="50" :max="5000" :step="10" show-input size="small" style="width: 380px;" />
            </el-form-item>
            <!-- <el-form-item label="恢复阈值 (米)">
              <el-input-number v-model="form.recoverThresholdM" :min="10" :max="form.thresholdM" size="small" style="width: 140px;" />
              <span class="hint-text">触发阈值 × 0.8 = {{ Math.round(form.thresholdM * 0.8) }}m（推荐迟滞防抖）</span>
              <el-button size="mini" style="margin-left: 8px;" @click="form.recoverThresholdM = Math.round(form.thresholdM * 0.8)">自动计算</el-button>
            </el-form-item> -->
            <!-- <el-form-item label="触发条件 (AND)">
              <div class="cond-row">
                <div class="cond-item">
                  <el-checkbox v-model="form._enPointCount" label="连续点数">
                    <el-input-number v-model="form.triggerPointCount" :min="1" :max="20" size="mini" style="width: 90px; margin-left: 6px;" /> 点
                  </el-checkbox>
                </div>
                <div class="cond-item">
                  <el-checkbox v-model="form._enDuration" label="持续时长">
                    <el-input-number v-model="form.triggerDurationS" :min="0" :max="1800" :step="10" size="mini" style="width: 90px; margin-left: 6px;" /> 秒
                  </el-checkbox>
                </div>
                <div class="cond-item">
                  <el-checkbox v-model="form._enDistMileage" label="持续里程">
                    <el-input-number v-model="form.triggerDistanceM" :min="0" :max="10000" :step="50" size="mini" style="width: 100px; margin-left: 6px;" /> 米
                    <span class="hint-text">0=不启用</span>
                  </el-checkbox>
                </div>
              </div>
            </el-form-item> -->
            <!-- <el-form-item label="回归条件">
              <el-input-number v-model="form.recoverPointCount" :min="1" :max="20" size="small" style="width: 120px;" />
              <span class="hint-text" style="margin-left: 6px;">连续 N 个点回归阈值内则判定回归</span>
            </el-form-item> -->
            <!-- <el-form-item label="运动门控">
              <el-switch v-model="form.onlyWhenMoving" active-text="开启" inactive-text="关闭" />
              <span class="hint-text" style="margin-left: 8px;">
                仅在速度 ≥
                <el-input-number v-model="form.minSpeedKmh" :min="0" :max="30" size="mini" style="width: 90px; margin: 0 4px;" />
                km/h 时判定（过滤停车静态漂移）
              </span>
            </el-form-item> -->

            <div class="form-group-title" style="margin-top: 12px;"><i class="el-icon-bell"></i> 告警通知</div>
            <!-- <el-form-item label="告警级别">
              <el-radio-group v-model="form.alarmLevel">
                <el-radio :label="1">提示</el-radio>
                <el-radio :label="2">重要</el-radio>
                <el-radio :label="3">紧急</el-radio>
              </el-radio-group>
            </el-form-item> -->
            <el-form-item label="通知渠道">
              <el-checkbox-group v-model="form.notifyChannels">
                <!-- <el-checkbox label="INAPP">站内信</el-checkbox> -->
                <el-checkbox label="SMS">短信</el-checkbox>
                <el-checkbox label="PUSH">APP推送</el-checkbox>
              
              </el-checkbox-group>
            </el-form-item>
            <el-form-item label="接收人">
              <el-select
                v-model="form._receiversText"
                multiple
                filterable
                allow-create
                default-first-option
                placeholder="输入名称后回车新增，例如：调度组, 王主管"
                size="small"
                style="width: 440px;"
              >
                <el-option v-for="op in operators" :key="op.id" :label="op.name" :value="op.name" />
              </el-select>
            </el-form-item>
            <!-- <el-form-item label="静默期 (分钟)">
              <el-input-number v-model="form.silenceMinutes" :min="0" :max="1440" size="small" style="width: 120px;" />
              <span class="hint-text" style="margin-left: 6px;">同一事件持续偏离期间 N 分钟内不重复通知</span>
            </el-form-item>
            <el-form-item label="持续提醒 (分钟)">
              <el-input-number v-model="form.repeatNotifyMinutes" :min="0" :max="1440" size="small" style="width: 120px;" />
              <span class="hint-text" style="margin-left: 6px;">0=关闭；>0 则每 N 分钟提醒一次“仍在偏离”</span>
            </el-form-item> -->
            <!-- <el-form-item label="回归通知">
              <el-switch v-model="form.notifyOnRecover" active-text="开启" inactive-text="关闭" />
              <span class="hint-text" style="margin-left: 6px;">车辆回归线路时是否发送通知</span>
            </el-form-item> -->
          </el-form>

          <!-- 配置摘要 -->
          <div class="summary-card">
            <div class="summary-title"><i class="el-icon-document-copy"></i> 配置摘要</div>
            <ul class="summary-list">
              <li><b>任务：</b>{{ form.name || '(未填写)' }}</li>
              <li><b>线路：</b>{{ selectedRoute ? selectedRoute.name : '未选择' }}</li>
              <li><b>车辆：</b>{{ selectedVehicles.length }} 辆</li>
              <li><b>有效期：</b>{{ (form._validRange || []).join(' 至 ') || '未设置' }}</li>
              <li><b>生效：</b>{{ form.effectiveType === 2 ? '周期 · ' + (formatWeekdays(form.weekdays) || '—') : '单次' }}</li>
              <li v-if="form.effectiveType === 2"><b>时段：</b>{{ (form.dailySlots || []).map(s => s.start + '-' + s.end).join(', ') || '—' }}</li>
              <li><b>阈值：</b>触发 {{ form.thresholdM }}m · 回归 {{ form.recoverThresholdM }}m</li>
              <li><b>触发：</b>{{ form.triggerPointCount }} 点 + {{ form.triggerDurationS }} 秒</li>
              <li><b>告警：</b>{{ alarmLevelText(form.alarmLevel) }} · {{ form.notifyChannels.join(',') || '无渠道' }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button size="small" @click="dialogVisible = false">取消</el-button>
      <el-button v-if="currentStep > 0" size="small" @click="currentStep--">上一步</el-button>
      <el-button v-if="currentStep < 3" size="small" type="primary" @click="goNext">下一步</el-button>
      <el-button v-if="currentStep === 3" size="small" type="primary" :loading="saving" @click="handleSubmit">
        {{ isEdit ? '保存修改' : '创建任务' }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import {
  mockCheckBindingConflict
} from '../../_mock/routeDeviationMock.js';
import { mapState } from 'vuex';
import KingLongMap from '@/components/KingLongMap.vue';

export default {
  name: 'BindingFormDialog',
  components: { KingLongMap },
  props: {
    visible: { type: Boolean, default: false },
    bindingId: { type: [Number, String], default: null }
  },
  data() {
    return {
      loading: false,
      saving: false,
      currentStep: 0,
      form: this.emptyForm(),
      // routes
      routes: [],
      routeSearchKw: '',
      // vehicles
      vehiclesPool: [],
      vehicleKw: '',
      selectedVehicles: [],
      selectAll: false,
      selectIndeterminate: false,
      conflicts: [],
      // 接收人（告警策略）
      operators: [],
      // 线路轨迹（通过车辆轨迹拉取用于预览）
      routeTrackCoords: null,
      // 地图
      routeMapReady: false
    };
  },
  computed: {
    dialogVisible: {
      get() { return this.visible; },
      set(v) { this.$emit('update:visible', v); }
    },
    isEdit() { return !!this.bindingId; },
    ...mapState({
      operator: state => state.user.operator
    }),
    filteredRoutes() {
      const kw = (this.routeSearchKw || '').toLowerCase().trim();
      return this.routes.filter(r => {
        if (!kw) return true;
        return (r.name || '').toLowerCase().includes(kw) ||
               (r.code || '').toLowerCase().includes(kw);
      });
    },
    selectedRoute() {
      return this.routes.find(r => r.id === this.form.routeId) || null;
    },
    filteredVehicles() {
      const kw = (this.vehicleKw || '').toLowerCase().trim();
      if (!kw) return this.vehiclesPool;
      return this.vehiclesPool.filter(v =>
        (v.plateNo || '').toLowerCase().includes(kw) ||
        (v.driver || '').toLowerCase().includes(kw)
      );
    }
  },
  watch: {
    selectedRoute(r) {
      this.routeTrackCoords = null;   // 切换线路时清空旧轨迹，等待重新拉取
      this.$nextTick(() => this.drawRouteOnMap());
      // 线路选中后，通过详情接口获取车辆与时间窗，自动拉取车辆轨迹显示
      if (r && r.id) this.loadRouteTrackForPreview(r);
    },
    visible: {
      handler(v) {
        if (v) {
          this.currentStep = 0;
          this.form = this.emptyForm();
          this.selectedVehicles = [];
          this.conflicts = [];
          this.routeTrackCoords = null;
          // 先加载基础数据（车辆池/操作员），再回显详情（回显需解析车牌和接收人名称）
          Promise.all([this.loadBases(), this.loadOperators()])
            .catch(() => {})
            .then(() => {
              if (this.bindingId) this.loadDetail();
            });
        }
      }
    }
  },
  methods: {
    emptyForm() {
      return {
        id: null,
        name: '',
        routeId: null,
        status: 1,
        thresholdM: 300,
        recoverThresholdM: 240,
        triggerPointCount: 3,
        triggerDurationS: 60,
        triggerDistanceM: 0,
        recoverPointCount: 3,
        onlyWhenMoving: true,
        minSpeedKmh: 5,
        effectiveType: 2,
        _validRange: [],
        validFrom: '',
        validTo: '',
        weekdays: [1, 2, 3, 4, 5],
        dailySlots: [{ start: '07:30', end: '09:00' }],
        timezone: 'Asia/Shanghai',
        alarmLevel: 2,
        notifyChannels: ['INAPP', 'SMS'],
        _receiversText: [],
        notifyReceivers: [],
        silenceMinutes: 10,
        repeatNotifyMinutes: 0,
        notifyOnRecover: true,
        _enPointCount: true,
        _enDuration: true,
        _enDistMileage: false
      };
    },
    loadBases() {
      // 车辆数据源：真实接口，参数参考车辆信息页（operator 来自登录用户）
      return Promise.all([
        // 线路数据源：真实接口查询有效线路（1=有效）
        this.$api.QueryPLRouteInfo({ QueryData: '', RouteStatus: 1, PageNo: 1, PageSize: 100 }),
        this.$api.queryVehicleinfoList({
          companyID: this.operator.CompanyId,
          pageNo: 1,
          pageSize: 1000,
          BranchName: '',
          OperatorID: this.operator.OperatorId
        })
      ]).then(([r1, r2]) => {
        if (r1.code === 200) {
          this.routes = (r1.data || []).map(item => ({
            id: item.RouteID,
            name: item.RouteName,
            code: item.RouteNo,
            totalLengthKm: item.RunMileage,
            pointCount: item.PointCount,
            sourceVehiclePlate: item.CarNo,
            extraPointCount: item.AddPointCount,
            status: item.RouteStatus,
            statusText: item.RouteStatus === 1 ? '有效' : '无效',
            version: item.RouteVersion,
            updatedAt: item.UpdateTime
          }));
        }
        if (r2.code === 200) {
          this.vehiclesPool = (r2.data || []).map(item => ({
            vehicleId: item.vID,
            plateNo: item.carno,
            driver: item.driver,
            driverPhone: item.telphoneNo
          }));
        }
      });
    },
    // 接收人数据源：真实接口查询操作员（OperatorID 来自登录用户）
    loadOperators() {
      return this.$api.queryOperatorList({
        OperatorID: this.operator.OperatorId,
        OperatorName: '',
        PageNo: 1,
        PageSize: 100
      }).then(res => {
        if (res.code === 200 && res.data && res.data.data) {
          this.operators = res.data.data
            .map(item => ({
              id: item.OPT_ID,
              name: item.OPTName || item.OPTNickName || ''
            }))
            .filter(op => op.name);
        }
      });
    },
    loadDetail() {
      this.loading = true;
      this.$api.QueryMonitoringTaskByID({ TaskID: this.bindingId }).then(res => {
        this.loading = false;
        if (res.code === 200 && res.data) {
          const d = res.data;
          const base = this.emptyForm();
          const f = {
            ...base,
            id: d.TaskID,
            name: d.TaskName || '',
            routeId: d.RouteID,
            status: d.TaskStatus,
            thresholdM: d.ThresholdValue ?? base.thresholdM,
            effectiveType: d.EffMode ?? 2,
            _validRange: [this.toDateStr(d.EffStartDate), this.toDateStr(d.EffEndDate)].filter(Boolean),
            validFrom: this.toDateStr(d.EffStartDate),
            validTo: this.toDateStr(d.EffEndDate),
            weekdays: d.WeekDays ? d.WeekDays.split(',').map(Number).filter(Boolean) : base.weekdays.slice(),
            dailySlots: (d.SlotInfo || []).map(s => ({
              start: this.toHHmm(s.SlotStartTime),
              end: this.toHHmm(s.SlotEndTime)
            })),
            alarmLevel: d.AlertLevel === 2 ? 3 : (d.AlertLevel === 1 ? 1 : base.alarmLevel),
            notifyChannels: d.IsEnabled === 0 ? [] : (d.NotifyMethod === 1 ? ['SMS'] : ['PUSH'])
          };
          if (!f.dailySlots.length) f.dailySlots = base.dailySlots.slice();
          this.form = f;
          // 车辆回显：VehicleList 逗号分隔的车辆ID，从车辆池解析车牌
          this.selectedVehicles = (d.VehicleList || '')
            .split(',')
            .map(s => s.trim())
            .filter(Boolean)
            .map(id => {
              const poolV = this.vehiclesPool.find(v => String(v.vehicleId) === String(id));
              return poolV ? { ...poolV } : { vehicleId: Number(id), plateNo: '车辆' + id, driver: '' };
            });
          // 接收人回显：操作员ID -> 名称（未匹配到的保留ID）
          this.form._receiversText = (d.OperatorList || '')
            .split(',')
            .map(s => s.trim())
            .filter(Boolean)
            .map(id => {
              const op = this.operators.find(o => String(o.id) === String(id));
              return op ? op.name : id;
            });
          this.form.notifyReceivers = this.form._receiversText.slice();
          this.syncSelection();
          this.checkConflicts();
        }
      }).catch(() => { this.loading = false; });
    },
    // 服务端 ISO/带时分时间转 YYYY-MM-DD（"2026-09-08T00:00:00Z" -> "2026-09-08"）
    toDateStr(str) {
      if (!str) return '';
      return String(str).replace('T', ' ').replace(/\.\d+Z$/, '').replace(/Z$/, '').trim().slice(0, 10);
    },
    // "HH:mm:ss" -> "HH:mm"
    toHHmm(str) {
      if (!str) return '';
      const m = String(str).match(/^(\d{2}:\d{2})/);
      return m ? m[1] : str;
    },
    selectRoute(r) {
      this.form.routeId = r.id;
    },
    fmtPt(p) {
      if (!p) return '—';
      return `${Number(p[0]).toFixed(4)}, ${Number(p[1]).toFixed(4)}`;
    },
    // ====== 地图预览 ======
    onPreviewMapLoaded() {
      this.routeMapReady = true;
      this.$nextTick(() => this.drawRouteOnMap());
    },
    // 通过线路详情获取 VehicleID 与 EffStartTime/EffEndTime，自动拉取车辆轨迹用于预览
    loadRouteTrackForPreview(r) {
      this.$api.QueryPLRouteInfoByID({ RouteID: r.id }).then(res => {
        if (res.code === 200 && res.data) {
          const d = res.data;
          const start = this.toDateTimeStr(d.EffStartTime);
          const end = this.toDateTimeStr(d.EffEndTime);
          if (!d.VehicleID || !start || !end) return;
          this.$api.QueryVehicleTrackInfo({
            VehicleId: String(d.VehicleID),
            StartTime: start,
            EndTime: end,
            Flag: 2,
            ParkDuration: 1,
            LanguageId: 1
          }).then(tr => {
            if (tr.code === 200 && tr.data) {
              const gpsData = tr.data.GpsData || [];
              if (!gpsData.length) return;
              this.routeTrackCoords = gpsData.map(g => ({ lng: g.Longitude, lat: g.Latitude }));
              this.$nextTick(() => this.drawRouteOnMap());
            }
          });
        }
      });
    },
    drawRouteOnMap() {
      const map = this.$refs.previewMap;
      if (!map || !this.routeMapReady) return;
      map.clearAll();
      const r = this.selectedRoute;
      if (!r) return;
      // 轨迹坐标：优先用车辆轨迹，其次线路自带几何
      const coords = this.routeTrackCoords ||
        ((r.geom || []).length ? r.geom.map(p => ({ lng: p[0], lat: p[1] })) : []);
      if (!coords.length) return;
      // 线路轨迹
      map.addPolyline('preview_route', r.name || '线路', coords, 5, '#409EFF', true, true);
      // 起终点
      const start = coords[0], end = coords[coords.length - 1];
      map.addMarker('pv_start', '起点', start.lng, start.lat, 'assets/svgs/ic_dingwei_qidian_lv@2x.png', null, 27, 36);
      map.addMarker('pv_end', '终点', end.lng, end.lat, 'assets/svgs/ic_dingwei_zhongdian_hong@2x.png', null, 27, 36);
      // 附加点
      (r.extraPoints || []).forEach((ep, i) => {
        if (ep.pointType === 2) {
          map.addCircle('pv_ep_c_' + i, { lng: ep.lng, lat: ep.lat }, ep.radiusM || 200,
            2, '#E6A23C', 'rgba(230,162,60,0.12)', false);
          map.addMarker('pv_ep_' + i, ep.name || '豁免点', ep.lng, ep.lat, null, null, 12, 12, { color: '#E6A23C' });
        } else {
          map.addMarker('pv_ep_' + i, ep.name || '途经点', ep.lng, ep.lat, null, null, 12, 12, { color: '#909399' });
        }
      });
    },
    // 服务端 ISO 时间("2026-09-07T00:00:00Z")转 "yyyy-MM-dd HH:mm:ss"
    toDateTimeStr(str) {
      if (!str) return '';
      return String(str).replace('T', ' ').replace(/\.\d+Z$/, '').replace(/Z$/, '').trim();
    },
    onVehicleSelection(rows) {
      const ids = new Set(this.selectedVehicles.map(v => v.vehicleId));
      rows.forEach(v => { if (!ids.has(v.vehicleId)) this.selectedVehicles.push(v); });
      const keepIds = new Set(rows.map(v => v.vehicleId));
      const filterFn = (v) => {
        // 如果v在filteredVehicles范围内且不在rows中，需要移除
        const inView = this.filteredVehicles.some(fv => fv.vehicleId === v.vehicleId);
        if (inView && !keepIds.has(v.vehicleId)) return false;
        return true;
      };
      this.selectedVehicles = this.selectedVehicles.filter(filterFn);
      this.updateSelectState();
      this.checkConflicts();
    },
    toggleSelectAll(val) {
      if (val) {
        const ids = new Set(this.selectedVehicles.map(v => v.vehicleId));
        this.filteredVehicles.forEach(v => {
          if (!ids.has(v.vehicleId)) this.selectedVehicles.push(v);
        });
      } else {
        const filteredIds = new Set(this.filteredVehicles.map(v => v.vehicleId));
        this.selectedVehicles = this.selectedVehicles.filter(v => !filteredIds.has(v.vehicleId));
      }
      this.syncSelection();
      this.checkConflicts();
    },
    syncSelection() {
      if (!this.$refs.vehTbl) return;
      this.$nextTick(() => {
        const ids = new Set(this.selectedVehicles.map(v => v.vehicleId));
        const toggles = this.vehiclesPool.filter(v => ids.has(v.vehicleId));
        toggles.forEach(v => this.$refs.vehTbl.toggleRowSelection(v, true));
        this.updateSelectState();
      });
    },
    updateSelectState() {
      const ids = new Set(this.selectedVehicles.map(v => v.vehicleId));
      const fvs = this.filteredVehicles;
      const inSel = fvs.filter(v => ids.has(v.vehicleId)).length;
      this.selectAll = fvs.length > 0 && inSel === fvs.length;
      this.selectIndeterminate = inSel > 0 && inSel < fvs.length;
    },
    removeVehicle(v) {
      this.selectedVehicles = this.selectedVehicles.filter(x => x.vehicleId !== v.vehicleId);
      this.syncSelection();
      this.checkConflicts();
    },
    checkConflicts() {
      if (!this.selectedVehicles.length) { this.conflicts = []; return; }
      mockCheckBindingConflict({
        vehicleIds: this.selectedVehicles.map(v => v.vehicleId),
        bindingId: this.bindingId || undefined
      }).then(res => { if (res.code === 200) this.conflicts = res.data || []; });
    },
    addSlot() { this.form.dailySlots.push({ start: '12:00', end: '14:00' }); },
    removeSlot(i) { this.form.dailySlots.splice(i, 1); },
    formatWeekdays(arr) {
      if (!arr || !arr.length) return '';
      const map = { 1:'一', 2:'二', 3:'三', 4:'四', 5:'五', 6:'六', 7:'日' };
      return arr.slice().sort().map(n => '周' + map[n]).join(',');
    },
    alarmLevelText(l) {
      return l === 3 ? '紧急' : l === 2 ? '重要' : '提示';
    },
    goNext() {
      if (this.currentStep === 0 && !this.form.routeId) {
        this.$message.warning('请选择一条基准线路'); return;
      }
      if (this.currentStep === 1 && !this.selectedVehicles.length) {
        this.$message.warning('请至少选择一辆车'); return;
      }
      if (this.currentStep === 2) {
        if (!this.form._validRange || this.form._validRange.length !== 2) {
          this.$message.warning('请设置总有效期'); return;
        }
        if (this.form.effectiveType === 2) {
          if (!this.form.weekdays || !this.form.weekdays.length) { this.$message.warning('请选择星期'); return; }
          if (!this.form.dailySlots || !this.form.dailySlots.some(s => s.start && s.end)) {
            this.$message.warning('请至少配置一个每日时段'); return;
          }
          for (const s of this.form.dailySlots) {
            if (!s.start || !s.end) { this.$message.warning('请完善每个时段的开始与结束时间'); return; }
            if (s.start === s.end) { this.$message.warning(`时段 ${s.start}-${s.end} 的开始时间不能等于结束时间`); return; }
          }
        }
      }
      this.currentStep++;
    },
    validateFinal() {
      if (!this.form.name || !this.form.name.trim()) {
        this.$message.warning('请填写任务名称');
        return false;
      }
      if (!this.form.routeId) { this.$message.warning('请选择线路'); this.currentStep = 0; return false; }
      if (!this.selectedVehicles.length) { this.$message.warning('请选择车辆'); this.currentStep = 1; return false; }
      if (this.form.effectiveType === 2 && this.form.dailySlots && this.form.dailySlots.length) {
        for (const s of this.form.dailySlots) {
          if (!s.start || !s.end || s.start === s.end) {
            this.$message.warning('每日时段的开始时间不能等于结束时间'); this.currentStep = 2; return false;
          }
        }
      }
      if (this.form.recoverThresholdM >= this.form.thresholdM) {
        this.$message.warning('恢复阈值必须小于触发阈值'); this.currentStep = 3; return false;
      }
      return true;
    },
    handleSubmit() {
      if (!this.validateFinal()) return;
      const _validRange = this.form._validRange || [];
      // 接收人名称 -> 操作员ID（未匹配到ID的自定义名称忽略）
      const receiverIds = this.operators
        .filter(op => (this.form._receiversText || []).includes(op.name))
        .map(op => op.id);
      const payload = {
        TaskID: this.form.id || -1,                       // 新建为-1
        TaskName: this.form.name,
        RouteID: this.form.routeId,
        VehicleList: this.selectedVehicles.map(v => v.vehicleId).join(','),
        EffMode: this.form.effectiveType,                 // 1次数 2周期
        EffStartDate: _validRange[0] ? this.moment(_validRange[0]).format('YYYY-MM-DD') : '',
        EffEndDate: _validRange[1] ? this.moment(_validRange[1]).format('YYYY-MM-DD') : '',
        WeekDays: (this.form.weekdays || []).join(','),   // 数字1-7
        SlotInfo: (this.form.dailySlots || []).map((s, i) => ({
          SlotStartTime: (s.start || '') + ':00',
          SlotEndTime: (s.end || '') + ':00',
          SlotSort: i + 1
        })),
        TaskStatus: this.form.status ?? 1,                // 1启用 0未启用
        ThresholdValue: this.form.thresholdM,
        AlertLevel: this.form.alarmLevel >= 3 ? 2 : 1,    // 1一般 2严重（紧急映射为严重）
        NotifyMethod: (this.form.notifyChannels || []).includes('SMS') ? 1 : 2, // 1短信 2APP推送
        OperatorList: receiverIds.join(','),
        IsEnabled: (this.form.notifyChannels || []).length ? 1 : 0  // 是否开启通知
      };
      this.saving = true;
      this.$api.SaveMonitoringTask(payload).then(res => {
        this.saving = false;
        if (res.code === 200) {
          this.$message.success(payload.TaskID === -1 ? '创建成功' : '保存成功');
          this.$emit('saved', res.data);
        }
      }).catch(() => { this.saving = false; });
    }
  }
};
</script>

<style scoped>
.binding-form-dialog ::v-deep .el-dialog__body { padding: 14px 24px 0; }
.task-name-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}
.task-name-label { font-weight: 600; color: #303133; font-size: 13px; }
.task-name-label i { color: #409EFF; margin-right: 4px; }
.task-name-tip { color: #909399; font-size: 12px; }
.form-body { min-height: 520px; padding-bottom: 10px; }
.steps-bar { margin-bottom: 18px; }
.step-panel { min-height: 420px; }
.step-desc {
  padding: 10px 14px;
  background: #ecf5ff;
  color: #409EFF;
  border-radius: 4px;
  margin-bottom: 14px;
  font-size: 13px;
  line-height: 1.5;
}
.warn-inline {
  display: inline-block;
  margin-left: 12px;
  padding: 2px 8px;
  background: #fdf6ec;
  color: #E6A23C;
  border-radius: 3px;
}
.step1-layout { display: flex; gap: 14px; min-height: 440px; }
.route-list-wrap { width: 52%; display: flex; flex-direction: column; }
.route-search { margin-bottom: 10px; }
.route-list {
  flex: 1;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.route-card {
  padding: 10px 12px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}
.route-card:hover { border-color: #409EFF; background: #f5faff; }
.route-card.active { border-color: #409EFF; background: #ecf5ff; box-shadow: 0 0 0 2px rgba(64,158,255,0.1); }
.rc-name { font-weight: 600; color: #303133; display: flex; align-items: center; }
.rc-check {
  display: inline-flex; align-items: center; justify-content: center;
  width: 18px; height: 18px; border-radius: 50%;
  background: #409EFF; color: #fff;
  margin-right: 6px; font-size: 12px;
}
.rc-meta {
  display: flex; gap: 10px; margin-top: 4px;
  color: #606266; font-size: 12px; flex-wrap: wrap;
}
.rc-code { color: #909399; }
.rc-sub { color: #909399; font-size: 12px; margin-top: 2px; }
.route-preview-wrap {
  flex: 1;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.preview-title {
  padding: 8px 12px;
  background: #f5f7fa;
  border-bottom: 1px solid #ebeef5;
  font-weight: 600;
  color: #303133;
  font-size: 13px;
}
.preview-title i { color: #409EFF; margin-right: 4px; }
.preview-map {
  position: relative;
  flex: 1;
  min-height: 320px;
  border-radius: 4px;
  overflow: hidden;
}
.preview-title {
  padding: 8px 12px;
  background: #f5f7fa;
  border-bottom: 1px solid #ebeef5;
  font-weight: 600;
  color: #303133;
  font-size: 13px;
  display: flex;
  align-items: center;
}
.preview-title i { color: #409EFF; margin-right: 4px; }
.preview-meta { margin-left: auto; }
.preview-empty-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  background: #fafbfc;
  z-index: 5;
}
.preview-empty-overlay i { font-size: 48px; margin-bottom: 10px; }
.preview-legend {
  position: absolute;
  bottom: 10px;
  left: 10px;
  background: rgba(255,255,255,0.95);
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 11px;
  color: #606266;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  z-index: 10;
  border: 1px solid #ebeef5;
}
.preview-legend .lg-item { display: inline-flex; align-items: center; }
.preview-legend .lg-line { display: inline-block; width: 16px; height: 3px; border-radius: 2px; margin-right: 4px; }
.preview-legend .lg-line.blue { background: #409EFF; }
.preview-legend .lg-dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; margin-right: 4px; border: 1.5px solid #fff; box-shadow: 0 0 0 1px rgba(0,0,0,0.1); }
.preview-legend .lg-dot.green { background: #67C23A; }
.preview-legend .lg-dot.red { background: #F56C6C; }
.preview-legend .lg-buf { display: inline-block; width: 14px; height: 10px; background: rgba(230,162,60,0.18); border: 1px solid #E6A23C; border-radius: 3px; margin-right: 4px; }

.step2-layout { display: flex; gap: 14px; }
.vehicle-pool {
  flex: 1;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 10px;
  display: flex;
  flex-direction: column;
}
.pool-toolbar {
  display: flex; align-items: center;
  margin-bottom: 10px; flex-wrap: wrap; gap: 6px;
}
.selected-wrap {
  width: 340px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.selected-title {
  padding: 8px 12px;
  background: #f5f7fa;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  font-size: 13px;
}
.selected-list { flex: 1; overflow-y: auto; padding: 8px; min-height: 200px; }
.sv-item {
  display: flex; align-items: center; justify-content: space-between;
  padding: 6px 10px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  margin-bottom: 6px;
  background: #fff;
}
.sv-item.conflict { border-color: #E6A23C; background: #fdf6ec; }
.sv-main { display: flex; gap: 8px; align-items: center; }
.sv-plate { font-weight: 600; color: #303133; }
.sv-driver { color: #909399; font-size: 12px; }
.conflict-list {
  border-top: 1px solid #ebeef5;
  padding: 8px 12px;
  background: #fdf6ec;
}
.conflict-title { color: #E6A23C; font-weight: 600; font-size: 12px; margin-bottom: 4px; }
.conflict-item { font-size: 12px; color: #8a6d3b; line-height: 1.6; }
.empty-tip { color: #c0c4cc; font-size: 12px; text-align: center; padding: 28px 0; }

.time-form { padding: 4px 8px; }
.hint-text { color: #909399; font-size: 12px; margin-left: 6px; }
.quick-btns { display: inline-flex; gap: 4px; }
.daily-slots { display: flex; flex-direction: column; gap: 6px; }
.slot-item { display: flex; align-items: center; gap: 8px; }
.slot-sep { color: #909399; }

.strategy-split { display: flex; gap: 16px; }
.strategy-form { flex: 1; }
.form-group-title {
  font-weight: 600; color: #303133; font-size: 13px;
  margin: 6px 0 10px;
  padding-bottom: 4px;
  border-bottom: 1px dashed #ebeef5;
}
.form-group-title i { color: #409EFF; margin-right: 4px; }
.cond-row { display: flex; flex-direction: column; gap: 6px; }
.cond-item { color: #606266; }

.summary-card {
  width: 300px;
  flex-shrink: 0;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fafbfc;
  display: flex;
  flex-direction: column;
}
.summary-title {
  padding: 10px 14px;
  background: #ecf5ff;
  color: #409EFF;
  font-weight: 600;
  border-bottom: 1px solid #ebeef5;
}
.summary-title i { margin-right: 4px; }
.summary-list {
  list-style: none; padding: 12px 16px; margin: 0;
  font-size: 13px; line-height: 2;
}
.summary-list li { color: #606266; word-break: break-all; }
.summary-list b { color: #303133; margin-right: 6px; font-weight: 500; }
</style>
